import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth'; // Import Firebase auth
import { db } from '../firebase';
import GoldenCoinIcon from './GoldenCoinIcon';
import './WalletPage.css';

const WalletPage = () => {
  const [walletCoins, setWalletCoins] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const auth = getAuth();
    let unsubscribeFirestore = null; // To hold the Firestore listener cleanup function

    // Listen for changes in authentication state (the correct way)
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      // If there's an active Firestore listener from a previous user session, clean it up
      if (unsubscribeFirestore) {
        unsubscribeFirestore();
      }

      if (user) {
        // User is signed in, get their wallet data using their UID
        const customerId = user.uid;

        // Query the correct 'customer_wallets' collection
        const walletQuery = query(
          collection(db, 'customer_wallets'),
          where('customerId', '==', customerId)
        );

        // Set up the real-time listener for the wallet
        unsubscribeFirestore = onSnapshot(walletQuery, (walletSnapshot) => {
          if (walletSnapshot.empty) {
            setError("You don't have a wallet yet. Subscribe to a plan to start earning coins!");
            setWalletCoins(0);
            setTransactions([]);
          } else {
            const walletData = walletSnapshot.docs[0].data();
            const walletBalance = walletData.walletCoins || 0;
            setWalletCoins(walletBalance);

            const txns = Array.isArray(walletData.transactions)
              ? walletData.transactions.slice().sort((a, b) => {
                  const aTime = a.timestamp?.seconds ? a.timestamp.seconds * 1000 : new Date(a.timestamp).getTime();
                  const bTime = b.timestamp?.seconds ? b.timestamp.seconds * 1000 : new Date(b.timestamp).getTime();
                  return bTime - aTime;
                })
              : [];
            
            setTransactions(txns);
            setError(null); // Clear any previous errors
          }
          setLoading(false);
        }, (err) => {
          console.error("Error fetching wallet balance: ", err);
          setError("An error occurred while fetching your wallet balance.");
          setLoading(false);
        });

      } else {
        // User is signed out, show the login message
        setError("Please log in to view your wallet.");
        setWalletCoins(0);
        setTransactions([]);
        setLoading(false);
      }
    });

    // Cleanup function: unsubscribe from both listeners when the component unmounts
    return () => {
      unsubscribeAuth();
      if (unsubscribeFirestore) {
        unsubscribeFirestore();
      }
    };
  }, []); // The empty dependency array ensures this effect runs only once on mount

  // Helper to format timestamp (no changes here)
  const formatDate = (ts) => {
    if (!ts) return '';
    if (ts.seconds) {
      const date = new Date(ts.seconds * 1000);
      return date.toLocaleString();
    }
    const date = new Date(ts);
    return isNaN(date.getTime()) ? '' : date.toLocaleString();
  };

  // The JSX remains the same
  return (
    <div className="wallet-page-container">
      <h1>My Wallet</h1>
      <p>Your collected coins from subscriptions and activities.</p>

      <div className="wallet-card">
        {loading ? (
          <div className="wallet-loading">Calculating Balance...</div>
        ) : error ? (
          <>
            <div className="wallet-error" style={{ marginBottom: '1.5rem' }}>{error}</div>
            <div className="wallet-balance-header">Your Current Wallet Balance</div>
            <div className="wallet-balance-amount">
              <GoldenCoinIcon size={60} />
              <span>{walletCoins}</span>
            </div>
          </>
        ) : (
          <>
            <div className="wallet-balance-header">Your Current Wallet Balance</div>
            <div className="wallet-balance-amount">
              <GoldenCoinIcon size={60} />
              <span>{walletCoins}</span>
            </div>
            <div className="wallet-footer">
              Use these coins to purchase add-ons from the menu.
            </div>
          </>
        )}
      </div>

      {!loading && !error && (
        <div className="wallet-transactions-section">
          {/* ... transaction history JSX ... */}
        </div>
      )}
    </div>
  );
};

export default WalletPage;